import { useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { PhoneCall, PhoneOff, Video } from 'lucide-react';
import {
  selectIncomingCall,
  incomingCallCleared,
  activeCallSet,
} from '@/features/calls/callsSlice.js';
import { useAcceptCallMutation, useRejectCallMutation } from '@/features/calls/callsApi.js';
import { setPreGrabbedStream } from '@/features/calls/preGrabbedMedia.js';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar.js';
import { getInitials } from '@/lib/utils.js';

const RINGTONE_URL = '/sounds/ringtone.mp3';

export function IncomingCallSheet() {
  const incoming = useSelector(selectIncomingCall);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [accept] = useAcceptCallMutation();
  const [reject] = useRejectCallMutation();

  const isOpen = incoming != null;

  useEffect(() => {
    if (!isOpen) return;
    const el = audioRef.current;
    if (!el) return;
    el.currentTime = 0;
    const playPromise = el.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(() => {
        // ignore — autoplay can be blocked or file missing
      });
    }
    return () => {
      el.pause();
      el.currentTime = 0;
    };
  }, [isOpen]);

  if (!incoming) return null;

  const peerName = incoming.callerName || 'Контакт';
  const isVideo = incoming.type === 'video';

  const handleAccept = async () => {
    // iOS Safari (включая PWA) требует user gesture для getUserMedia. Берём
    // stream СИНХРОННО в click handler и сохраняем module-scoped — CallPage
    // подхватит готовые tracks через publishTrack(), без повторного gUM
    // (повторный вызов после await + navigate может потерять gesture chain).
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
        video: incoming.type === 'video',
      });
    } catch (err) {
      console.error('media permission denied', err);
      // Если юзер отказал — сообщим и закроем входящий
      dispatch(incomingCallCleared());
      return;
    }
    setPreGrabbedStream(stream);

    try {
      const data = await accept(incoming.callId).unwrap();
      dispatch(
        activeCallSet({
          callId: data.callId,
          type: incoming.type,
          livekitUrl: data.livekitUrl,
          token: data.token,
          peer: {
            id: incoming.callerId,
            name: incoming.callerName,
            avatarUrl: incoming.callerAvatarUrl,
          },
          direction: 'incoming',
          conversationId: incoming.conversationId,
        }),
      );
      navigate(`/call/${data.callId}`);
    } catch (err) {
      console.error('accept failed', err);
      stream.getTracks().forEach((t) => t.stop());
      setPreGrabbedStream(null);
      dispatch(incomingCallCleared());
    }
  };

  const handleReject = async () => {
    try {
      await reject({ callId: incoming.callId, reason: 'rejected' }).unwrap();
    } catch {
      // ignore — server may already mark it rejected
    } finally {
      dispatch(incomingCallCleared());
    }
  };

  return (
    <DialogPrimitive.Root open={isOpen} modal>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-[100] bg-background/80 backdrop-blur-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content
          aria-describedby={undefined}
          onEscapeKeyDown={(e) => e.preventDefault()}
          onPointerDownOutside={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
          className="fixed inset-x-0 bottom-0 top-auto z-[100] flex flex-col items-center gap-6 border-t border-border-subtle bg-card p-6 pt-10 shadow-card-hover data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom sm:inset-auto sm:left-1/2 sm:top-1/2 sm:max-w-md sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-2xl sm:border"
        >
          <DialogPrimitive.Title className="sr-only">
            Входящий звонок от {peerName}
          </DialogPrimitive.Title>

          <div className="relative flex h-32 w-32 items-center justify-center">
            <span className="absolute inset-0 animate-ping rounded-full bg-primary/30" />
            <span className="absolute inset-2 animate-pulse rounded-full bg-primary/20" />
            <Avatar className="relative h-28 w-28 border-4 border-background shadow-lg">
              {incoming.callerAvatarUrl ? (
                <AvatarImage src={incoming.callerAvatarUrl} alt={peerName} />
              ) : null}
              <AvatarFallback className="text-2xl">{getInitials(peerName)}</AvatarFallback>
            </Avatar>
          </div>

          <div className="flex flex-col items-center gap-2 text-center">
            <h2 className="font-display text-h3 font-semibold text-foreground">{peerName}</h2>
            <p className="flex items-center gap-2 text-small text-muted-foreground">
              {isVideo ? <Video className="h-4 w-4" /> : <PhoneCall className="h-4 w-4" />}
              <span>Входящий {isVideo ? 'видео' : 'аудио'}звонок</span>
            </p>
          </div>

          <div className="mt-4 flex w-full items-center justify-center gap-12">
            <button
              type="button"
              onClick={handleReject}
              aria-label="Отклонить звонок"
              className="flex h-16 w-16 items-center justify-center rounded-full bg-destructive text-destructive-foreground shadow-lg shadow-destructive/30 transition-all hover:bg-destructive/90 active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <PhoneOff className="h-7 w-7" />
            </button>
            <button
              type="button"
              onClick={handleAccept}
              aria-label="Принять звонок"
              className="flex h-16 w-16 animate-pulse items-center justify-center rounded-full bg-emerald-500 text-white shadow-lg shadow-emerald-500/40 transition-all hover:bg-emerald-600 hover:animate-none active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <PhoneCall className="h-7 w-7" />
            </button>
          </div>

          <audio ref={audioRef} src={RINGTONE_URL} loop preload="auto" className="hidden" />
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
